import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ISession } from "../../../models/session";
import moment from "moment-timezone";
import { 
  formatDateForGrouping, 
  getTodayFormatted, 
  findClosestDate as findClosestDateUtil, 
  compareDates, 
  compareTimes 
} from "../../../utils/dateUtils";

import EventLoader from "./components/EventLoader";
import EventHeader from "./components/EventHeader";
import SwiperControl from "../../core/swiper/Swiper";
import EventSession from "../../content/Events/Event/EventSession";
import { AddToCalendarButton } from "../../core/AddToCalendarButton";
import {
  setCurrentEvent,
  setCurrentPage,
  setSidebarRouteData,
  setCurrentParticipants,
} from "../../../store/actions/mainActions";
import {
  setIsProgramTeam,
  setTasks,
  setTotalEventTasks,
  setTotalEventTasksLoading,
  setUserCompletedEventTasks,
} from "../../../store/actions/contentActions";
import { setSelectedDay } from "../../../store/actions/current_userActions";

import eventsService from "../../../Axios/services/events.service";
import usersService from "../../../Axios/services/users.service";
import {
  filterSessionsByUser,
  filterValidClasses,
  groupKey,
  sortSessionsByOrder,
  userOwnsEvent,
} from "./utils/utils";
import styles from "./EventPage.module.scss";
import "./EventPage.scss";
import EventRegistration from "./components/EventRegistration";
import EventDetails from "./components/EventDetails";
import { useLocation } from "react-router-dom";
import { countCompletedTasks } from "../EventPageTasks/components/EventTask/TaskBlock/utils/blockUtils";
import { isMobileApp } from "../../../helpers/helpers";

export const EventPage = () => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const location = useLocation();

  const { id } = useParams();
  const { me } = useSelector((state: any) => state.auth);
  const { currentEvent, currentParticipantsEvent } = useSelector(
    (state: any) => state.main
  );
  const { isTotalEventTasksLoading } = useSelector(
    (state: any) => state.content
  );
  const { classList, selectedDay } = useSelector(
    (state: any) => state.current_user
  );
  const { fromMyCampus = false } = location.state || {};

  const [event, setEvent] = useState<any>(undefined);

  const [entityLanguage, setEntityLanguage] = useState<string>("common");
  const [eventBack, setEventBack] = useState<any>(undefined);
  const [eventDays, setEventDays] = useState<any>([]);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const isUserRegistered = userOwnsEvent(event, me);
  const isNotEnglish = entityLanguage !== "English";

  const [t] = useTranslation(entityLanguage);

  useEffect(() => {
    if (fromMyCampus && currentEvent?._id !== id) {
      dispatch(setTotalEventTasksLoading(true));
    }
    fetchEventData();
    return () => {
      dispatch(setSidebarRouteData({}));
      dispatch(setCurrentPage(""));
    };
  }, [me]);

  const fetchEventData = () => {
    try {
      Promise.all([getEventSessions(), getEventParticipants()]);
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  };

  const getEventSessions = async () => {
    const fetchedEvent =
      currentEvent?._id === id
        ? currentEvent
        : await eventsService.getEvent(id);

    if (!fetchedEvent || !me) return;

    const { sessions, language } = fetchedEvent;

    me.classes = filterValidClasses(me.classes);
    const { userClasses, isProgramTeam } = filterSessionsByUser(sessions, me);
    getUsersTasks(isProgramTeam);
    fetchedEvent.sessions = userClasses;

    setEntityLanguage(language);
    localStorage.setItem("entityLanguage", language);

    setEvent(fetchedEvent);
    setEventBack(fetchedEvent);
    dispatch(setCurrentEvent(fetchedEvent));
    openSessionForToday(fetchedEvent);
    const sessionDays = groupSessionsByDay(fetchedEvent);
    setEventDays(sessionDays);

    if (userOwnsEvent(fetchedEvent, me)) {
      dispatch(setCurrentPage("EventPage"));
      dispatch(setSidebarRouteData({ id }));
    }
  };

  const groupSessionsByDay = (event: any) => {
    if (event.sessions && Array.isArray(event.sessions)) {
      const sessions = [...event.sessions];
      (sessions || []).sort((a: any, b: any) =>
        compareDates(a.startDate, b.startDate)
      );
      const daysArr = new Set();
      return sessions.reduce((acc: any, session: any) => {
        const date = formatDateForGrouping(session.startDate);
        if (!acc[date]) {
          acc[date] = [];
        }

        acc[date].push(session);
        if (session.sessionStatus !== "Hidden") {
          daysArr.add(date);
        }

        return Array.from(daysArr);
      }, []);
    }
    return [];
  };

  const findClosestDate = (dateArray: string[]): string | null => {
    return findClosestDateUtil(dateArray);
  };

  const openSessionForToday = (event) => {
    const eventDaysArr = event ? groupSessionsByDay(event) : [];
    let closestDay: string;

    if (eventDaysArr) {
      closestDay = findClosestDate(eventDaysArr);
      dispatch(setSelectedDay(closestDay));
    }
  };

  const getSessionsByDay = (day) => {
    dispatch(setSelectedDay(day));

    if (day === "All") {
      resetSessions();
    } else if (day === "MyClasses") {
      mySessions();
    } else {
      if (
        eventBack &&
        eventBack.sessions &&
        Array.isArray(eventBack.sessions)
      ) {
        setShowAllSessions(false);
        const sessions = eventBack.sessions.filter((session: any) => {
          const date = formatDateForGrouping(session.startDate);
          // Handle both string dates and moment objects for day parameter
          const formattedDay = typeof day === 'string' ? day : formatDateForGrouping(day);
          return date === formattedDay;
        });

        sessions.sort((a, b) => (a.dayTime > b.dayTime ? 1 : -1));
      }
    }
  };

  const mySessions = () => {
    const filteredClassList = classList.filter(
      (cls: any) => cls.eventId === event._id
    );
    setEvent({ ...event, sessions: filteredClassList });
  };

  const resetSessions = () => {
    setEvent(eventBack);
  };

  const getUsersTasks = async (isProgramTeam: boolean) => {
    if (me) {
      const tasks = await eventsService.getSeminarTasksById(id, me._id);
      dispatch(setTotalEventTasks(tasks?.length || 0));
      dispatch(setTotalEventTasksLoading(false));
      dispatch(setUserCompletedEventTasks(countCompletedTasks(tasks)));
      dispatch(setTasks(tasks));
      dispatch(setIsProgramTeam(isProgramTeam));
    }
  };

  const getEventParticipants = async () => {
    const participants =
      currentEvent && currentEvent._id == id
        ? currentParticipantsEvent
        : await usersService.getParticipantsByEventId(id);
    if (participants) {
      me.participants = participants;
      dispatch(setCurrentParticipants(participants));
    }
  };

  const isSelectedSession = (session: ISession) => {
    console.log({ selectedDay }, { session });
    if (!showAllSessions) {
      const formattedDate = formatDateForGrouping(session.startDate);
      return formattedDate === selectedDay;
    }
    return true;
  };

  const groupSessionsByDateAndTime = (sessions: ISession[]) => {
    const grouped = sessions.reduce(
      (
        groups: Record<
          string,
          {
            startDate: string;
            startTime: string;
            endTime: string;
            sessions: ISession[];
          }
        >,
        session: ISession
      ) => {
        const key = groupKey(session);

        if (!groups[key]) {
          groups[key] = {
            startDate: session.startDate,
            startTime: session.startTime,
            endTime: session.endTime,
            sessions: [],
          };
        }
        groups[key].sessions.push(session);
        return groups;
      },
      {}
    );

    const sortedGroups = Object.values(grouped).sort((a, b) => {
      // Use consistent date comparison
      const dateComparison = compareDates(a.startDate, b.startDate);
      if (dateComparison !== 0) {
        return dateComparison;
      }

      // Compare times consistently
      return compareTimes(a.startTime, b.startTime);
    });

    sortedGroups.forEach((group) => {
      group.sessions.sort(sortSessionsByOrder);
    });
    return sortedGroups;
  };

  const formatDate = (date: string) => {
    const expectedFormat = "YYYY-MM-DD";
    if (moment(date, expectedFormat, true).isValid()) {
      return date;
    }

    return moment(date).format(expectedFormat);
  };

  if (isTotalEventTasksLoading || !event || !me?.events) return <EventLoader />;

  return (
    <div
      className={`event-page container-fluid ${styles.container} ${
        isNotEnglish ? "alignRight" : ""
      }`}
    >
      <div className="goBack" onClick={() => history("/my-campus")}>
        {t("Back to list")}
      </div>

      <EventHeader event={event} language={entityLanguage} />

      <div className="mb-md-4 mb-3 align-items-center justify-content-between">
        <EventDetails event={event} entityLanguage={entityLanguage} t={t} />

        {!isUserRegistered && <EventRegistration />}
      </div>
      {isUserRegistered && (
        <>
          <div className="e-agenda-title-container d-flex align-items-center">
            <div className="ctimeline">{t("Seminar Agenda")}</div>
            <button
              className={`${styles.showAllSessionsButton} ${
                isNotEnglish ? styles.isNotEnglish : ""
              }`}
              onClick={() => setShowAllSessions((prev) => !prev)}
            >
              {eventDays?.length > 1 &&
                (showAllSessions
                  ? t("Show Selected Day")
                  : t("Show All Sessions"))}
            </button>
            <div className="ctimeline">
              <AddToCalendarButton
                name={event.eventTitle}
                buttonStyle="round"
                hideTextLabelButton
                hideTextLabelList={isMobileApp()}
                startDate={formatDate(event.dtStartDate)}
                endDate={formatDate(event.dtEndDate)}
                location={
                  event.address.street +
                  " " +
                  event.address.city +
                  " " +
                  event.address.state +
                  " " +
                  event.address.zip
                }
                //options="'Apple|Apple is ok for me','Google|Add to Google Cal','Yahoo|Yahoo, really?'"
                options={["Apple", "Google", "Yahoo", "iCal"]}
              />
            </div>
          </div>

          {eventDays && eventDays.length > 1 && (
            <div
              className={`GroupedSessionsMenu ${
                entityLanguage === "English" ? "flex-row" : "flex-row-reverse"
              }`}
              dir={entityLanguage === "English" ? "ltr" : "ltr"}
            >
              <div className={`swiperContainer`}>
                <SwiperControl
                  dates={eventDays}
                  onDayClick={getSessionsByDay}
                  showAllSessions={showAllSessions}
                />
              </div>
            </div>
          )}

          {eventDays.length > 1 && (
            <div id="daysfilter" className="GroupedSessionsMenuMobile">
              <SwiperControl
                dates={eventDays}
                onDayClick={getSessionsByDay}
                showAllSessions={showAllSessions}
              />
            </div>
          )}

          {event.eventAgendaBehaviour !== "Hidden" || me.role !== "Member" ? (
            <div className="eventSessionsContainer">
              {event.sessions &&
                Array.isArray(event.sessions) &&
                groupSessionsByDateAndTime(
                  event.sessions.filter(
                    (session: ISession) =>
                      session.sessionStatus !== "Hidden" &&
                      isSelectedSession(session)
                  )
                ).map(({ sessions }, index) => {
                  return (
                    <EventSession
                      id={""}
                      key={index}
                      isFeed={false}
                      session={sessions}
                      communities={event.communities}
                      folderList={
                        event.folderListBySession
                          ? event.folderListBySession
                          : []
                      }
                      loggedUser={me}
                      language={entityLanguage}
                    />
                  );
                })}
            </div>
          ) : (
            <div className="cbrief mb-md-5 mb-3">
              (The agenda for this event will be published soon)
            </div>
          )}
        </>
      )}
    </div>
  );
};
